import {
  Box,
  Link,
  Loader,
  MenuMultiOption,
  ToggleButton,
  ToggleButtonGroup,
} from "@saviynt/design-system";
import { useIntl } from "react-intl";

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import PoliciesFilterControl from "./PoliciesFilterControl";
import PoliciesFilterOptions from "./PoliciesFilterOptions";
import { useGetFiltersList } from "../../hooks/usePolicy";
import {
  NHI_TAB,
  AI_AGENT_TAB,
  WORKFORCE_TAB,
} from "../../constants/policies.constants";
import { usePoliciesFilter } from "../../context/PoliciesFilterContext";
import { transformSnakeCaseToTitleCase } from "@repo/mfe-core";

/**
 * Search and filter bar for Policies.
 *
 * Provides search and filtering capabilities for the policy list with a collapsible
 * filter panel. The component uses a subtle background color that adapts to the theme.
 *
 * @returns Search and filter controls with no props (fully self-contained component)
 */

export interface PoliciesFilterValues {
  searchValue: string;
  selectedFilters: Record<string, MenuMultiOption[]>;
}

const transformFilterData = (
  filterData: Record<string, { options: Array<{ id: string; name: string }> }>,
) => {
  return Object.entries(filterData).map(([label, { options }]) => ({
    label: transformSnakeCaseToTitleCase(label),
    options: options.map(({ id, name }) => ({
      value: id,
      label:
        name.toLowerCase() === "true"
          ? "Yes"
          : name.toLowerCase() === "false"
            ? "No"
            : label === "tags"
              ? id
              : name,
      isChecked: false,
    })),
  }));
};

export default function PoliciesPageFilter() {
  const intl = useIntl();
  const { filterValues, setFilterValues, markFilterReady } =
    usePoliciesFilter();
  const [selection, setSelection] = useState(1);
  const [searchValue, onSearchChange] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<
    Record<string, MenuMultiOption[]>
  >({});
  const [clearTrigger, setClearTrigger] = useState(0);
  const { data, isLoading } = useGetFiltersList();
  const filterData = useMemo(
    () => (data ? transformFilterData(data) : []),
    [data],
  );

  // Track writes we initiate so the sync effect can skip them.
  const isInternalWriteRef = useRef(false);
  const writeFilterValues = useCallback(
    (values: PoliciesFilterValues) => {
      isInternalWriteRef.current = true;
      setFilterValues(values);
    },
    [setFilterValues],
  );

  // Sync local state when filterValues are changed externally (e.g. SaveViewMenu).
  useEffect(() => {
    if (isInternalWriteRef.current) {
      isInternalWriteRef.current = false;
      return;
    }
    const category = filterValues.selectedFilters.category?.[0]?.value;
    const newSelection =
      category === WORKFORCE_TAB ? 1 : category === NHI_TAB ? 2 : 1;
    onSearchChange(filterValues.searchValue);
    setSelectedFilters(filterValues.selectedFilters);
    setSelection(newSelection);
  }, [filterValues]);

  useEffect(() => {
    const categoryValue =
      selection === 1
        ? WORKFORCE_TAB
        : selection === 2
          ? NHI_TAB
          : AI_AGENT_TAB;
    const updated = {
      ...selectedFilters,
      category: [
        { value: categoryValue, label: categoryValue, isChecked: true },
      ],
    };
    setSelectedFilters(updated);
    writeFilterValues({ searchValue, selectedFilters: updated });
    markFilterReady();
  }, [selection]);

  const handleFilterClick = () => {
    setIsFilterOpen(!isFilterOpen);
  };

  const handleFilterOptionChange = (
    label: string,
    selectedOptions: MenuMultiOption[],
  ) => {
    const updated = { ...selectedFilters, [label]: selectedOptions };
    setSelectedFilters(updated);
    writeFilterValues({ searchValue, selectedFilters: updated });
  };

  const handleSearchChange = (value: string) => {
    onSearchChange(value);
    writeFilterValues({ searchValue: value, selectedFilters });
  };

  const handleClearAll = () => {
    const preserved = { category: selectedFilters.category };
    setSelectedFilters(preserved);
    setClearTrigger((prev) => prev + 1);
    writeFilterValues({ searchValue, selectedFilters: preserved });
  };

  return (
    <div className="PolicyPageFilter w-full flex flex-col gap-0 py-4 px-6 ">
      <Box className="flex items-center gap-3">
        <ToggleButtonGroup selection={selection} setSelection={setSelection}>
          <ToggleButton
            label={intl.formatMessage({
              defaultMessage: "Workforce",
              id: "4hEFzo",
            })}
          />
          <ToggleButton
            label={intl.formatMessage({
              defaultMessage: "Non-Human",
              id: "YM6EKm",
            })}
          />
          <ToggleButton
            label={intl.formatMessage({
              defaultMessage: "Agent",
              id: "QGVI63",
            })}
          />
        </ToggleButtonGroup>
        <PoliciesFilterControl
          searchValue={searchValue}
          onSearchChange={handleSearchChange}
          handleFilterClick={handleFilterClick}
          isFilterMenuOpen={isFilterOpen}
          hasFiltersSelected={
            !!Object.keys(selectedFilters).filter((k) => k !== "category")
              .length
          }
        />
      </Box>

      {isFilterOpen && (
        <>
          {isLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loader kind="loop" format="inline" color="brand" />
            </div>
          ) : (
            <Box className="flex gap-2 pt-2 items-center">
              {filterData.map(({ label, options }) => {
                const selectedValues = new Set(
                  (selectedFilters[label] ?? []).map((o) => o.value),
                );
                const mergedOptions = options.map((o) => ({
                  ...o,
                  isChecked: selectedValues.has(o.value),
                }));
                return (
                  <PoliciesFilterOptions
                    key={label}
                    label={label}
                    filterOptions={mergedOptions}
                    onFilterChange={handleFilterOptionChange}
                    clearTrigger={clearTrigger}
                  />
                );
              })}
              <Link
                text={intl.formatMessage({
                  defaultMessage: "Clear",
                  id: "3E8xVq",
                  description: "Button text to clear date filter selection",
                })}
                color="brand"
                onClick={handleClearAll}
                kind="button"
                aria-label={intl.formatMessage({
                  defaultMessage: "Clear all filters",
                  description:
                    "Aria label for button to clear all applied filters in the dashboard",
                  id: "HjHu6p",
                })}
              />
            </Box>
          )}
        </>
      )}
    </div>
  );
}
